import React from 'react';
import { Badge, Box, Button, Flex, Group, Paper, Text } from '@mantine/core';
import { IconArrowRight, IconFileText } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from './primitives';
import { HistoryItem } from './types';

interface HistoryBlockProps {
  items: HistoryItem[];
  onOpen: (id: string) => void;
  onOpenEssay?: (formId: string) => void;
}

export const HistoryBlock: React.FC<HistoryBlockProps> = ({ items, onOpen, onOpenEssay }) => {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <Box>
        <Eyebrow>{t('perfReview.home.history.section')}</Eyebrow>
        <Paper withBorder p="lg" radius="md" mt="sm">
          <Text c="dimmed" size="sm" ta="center">
            {t('perfReview.home.history.empty')}
          </Text>
        </Paper>
      </Box>
    );
  }
  return (
    <Box>
      <Flex justify="space-between" align="baseline" mb="sm">
        <Eyebrow>{t('perfReview.home.history.section')}</Eyebrow>
        <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {t('perfReview.home.history.count', { count: items.length })}
        </Text>
      </Flex>
      <Paper withBorder radius="md" p={0}>
        {items.map((h, i) => (
          <Flex
            key={h.id}
            align="center"
            gap="md"
            p="md"
            onClick={() => onOpen(h.id)}
            style={{
              cursor: 'pointer',
              borderBottom:
                i === items.length - 1 ? 'none' : '1px solid var(--mantine-color-default-border)',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = 'var(--mantine-color-default-hover)')
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Box style={{ flex: 1 }}>
              <Text size="sm" fw={500}>
                {h.title}
              </Text>
              <Text size="xs" c="dimmed" mt={2} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {h.period}
              </Text>
            </Box>
            <Badge color="gray" variant={h.closed ? 'light' : 'default'} size="sm">
              {h.closed
                ? t('perfReview.home.history.closed')
                : t('perfReview.home.history.archived')}
            </Badge>
            <Group gap="xs" wrap="nowrap">
              {h.selfFormId && onOpenEssay && (
                <Button
                  size="xs"
                  variant="light"
                  color="teal"
                  leftSection={<IconFileText size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenEssay(h.selfFormId!);
                  }}
                >
                  {t('perfReview.home.history.openEssay')}
                </Button>
              )}
              <Button
                size="xs"
                variant="subtle"
                rightSection={<IconArrowRight size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(h.id);
                }}
              >
                {t('perfReview.home.history.openResults')}
              </Button>
            </Group>
          </Flex>
        ))}
      </Paper>
    </Box>
  );
};
